import { apiUrl } from "../api/endpoints";
import React, { useState, useEffect, useContext, Fragment, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SuspendIcon from '@mui/icons-material/DoNotDisturbAltOutlined';
import TranferIcon from '@mui/icons-material/SyncAltOutlined';
import EditIcon from '@mui/icons-material/ModeOutlined';
import ShowIcon from '@mui/icons-material/VisibilityOffOutlined';
import HideIcon from '@mui/icons-material/VisibilityOutlined';
import InfoIcon from '@mui/icons-material/Info';
import { AddUser } from './AddUser';
import { EditUser } from './EditUser';
import { useAuth } from "../hooks/AuthProvider";
import { PasswordIcon } from '../Icons';
import DeleteIcon from '@mui/icons-material/Delete';

import axios from 'axios';
import '../Components/modal.css';
import './edit.css';
import './details.css';
import { TransferUser } from './UserDetails';
import { suspendUser, deleteUser, resetPassword, deleteMultiUsers } from '../lib/actions';
import moment from 'moment';
import DefaultIcon from '../Components/DefaultIcon';
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from '@mui/x-data-grid';

import { LanguageContext } from '../context/LanguageContext';
import userTranslations from '../languages/user.json';
import Alert from '../Components/Alert';
import { getErrorMessage } from '../utils/errorUtils';
import AppDialog from '../Components/AppDialog';
import { normalizeUserListRow } from '../utils/listRowIds';
import Spinner from '../Components/Spinner';

export const ChangeUserPassword = ({
  state,
  setState,
  canChangePassword,
  onSuccess,
  forceMode = false
}) => {
  const { logOut } = useAuth();
  const { lang } = useContext(LanguageContext);
  const tRoot = userTranslations[lang] || userTranslations.en;
  const t = tRoot.changePasswordModal;
  const isRtl = ['ar', 'ur'].includes(lang);

  const [passwordState, setPasswordState] = useState({
    password: '',
    status: 'very weak',
    show: false
  });
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right'
  });

  const showAlert = (type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: isRtl ? 'left' : 'right'
    });
  };

  const evaluatePassword = (password = '') => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    const strength = {
      0: 'very weak',
      1: 'very weak',
      2: 'weak',
      3: 'medium',
      4: 'good',
      5: 'great'
    };
    return strength[score];
  };

  const getPasswordChecks = (password = '') => ({
    minLength: password.length >= 8,
    hasLowercase: /[a-z]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[^A-Za-z0-9]/.test(password)
  });

  const passwordChecks = getPasswordChecks(passwordState.password);
  const passwordPolicyValid = Object.values(passwordChecks).every(Boolean);

  const passwordRules = [
    { key: 'minLength', label: t.ruleMinLength },
    { key: 'hasLowercase', label: t.ruleLowercase },
    { key: 'hasUppercase', label: t.ruleUppercase },
    { key: 'hasNumber', label: t.ruleNumber },
    { key: 'hasSpecial', label: t.ruleSpecial }
  ];
  const passwordStrengthLabels = {
    'very weak': t.statusVeryWeak,
    weak: t.statusWeak,
    medium: t.statusMedium,
    good: t.statusGood,
    great: t.statusGreat
  };

  if (!forceMode && !canChangePassword) {
    return (
      <div
        id="modal"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
        }}
      >
        <div
          id="popup"
          className="dashboard-modal"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
            fontSize:
              'calc(var(--body-font-size) * var(--font-size-multiplier))'
          }}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <PasswordIcon color={'var(--accent)'} />
            </div>
            <div className="dashboard-header-text">
              <h2>{t.permissionDeniedTitle}</h2>
              <p>{t.permissionDeniedMessage}</p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content dashboard-content" />

          <div className="modal-footer dashboard-footer">
            <button
              className="btn btn-cancel"
              onClick={() => setState({ open: false, userId: 0 })}
            >
              {t.buttonClose}
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }

  const changePassword = async () => {
    if (!forceMode && !canChangePassword) {
      showAlert('error', t.permissionDeniedMessage);
      return;
    }

    if (!passwordPolicyValid) {
      showAlert('error', t.passwordPolicyMessage);
      return;
    }

    try {
      const res = await axios.put(
        apiUrl(`user/update/${state.userId}`),
        { password: passwordState.password },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (res.status === 200 && res.data.status === 'OK') {
        const updatedUser = res.data.data;
        showAlert('success', t.successMessage);

        if (typeof onSuccess === 'function') {
          onSuccess(updatedUser);
        }

        if (!forceMode && setState) {
          setTimeout(() => {
            setState({ open: false, userId: 0 });
          }, 2000);
        }
      } else {
        throw new Error('Received an unexpected response from the server.');
      }
    } catch (err) {
      console.error('Change password failed:', err);
      const errorMessage = getErrorMessage(err);
      showAlert('error', errorMessage);
    }
  };

  return (
    <Fragment>
      <Alert
        open={alertState.open}
        close={() =>
          setAlertState({ ...alertState, open: false })
        }
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div
        id="modal"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
        }}
      >
        <div
          id="popup"
          className="dashboard-modal"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
            fontSize:
              'calc(var(--body-font-size) * var(--font-size-multiplier))'
          }}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <PasswordIcon color={'var(--accent)'} />
            </div>
            <div className="dashboard-header-text">
              <h2>{t.title}</h2>
              <p>{t.confirmText}</p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content dashboard-content">
            <div className="modal-error">
              <InfoIcon style={{ color: 'rgb(245, 34, 45)' }} />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <h4>{t.confirmTitle}</h4>
                <p>{t.confirmText}</p>
              </div>
            </div>

            <div className="edit-form-item" style={{ padding: '16px 0' }}>
              <label>{t.newPasswordLabel}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={passwordState.show ? 'text' : 'password'}
                  className="add-column"
                  placeholder={t.newPasswordLabel}
                  value={passwordState.password || ''}
                  style={{
                    color: 'var(--cell-color)',
                    caretColor: 'var(--cell-color)',
                    paddingInlineStart: 40
                  }}
                  onChange={e =>
                    setPasswordState({
                      ...passwordState,
                      password: e.target.value,
                      status: evaluatePassword(e.target.value)
                    })
                  }
                />
                <span
                  className="search-icon"
                  style={{
                    width: 'fit-content',
                    height: 'fit-content',
                    paddingInlineStart: 5,
                    cursor: 'pointer'
                  }}
                >
                  <PasswordIcon color={'var(--cell-color)'} />
                </span>
                <span
                  className="search-icon"
                  style={{
                    left: '90%',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                  onClick={() =>
                    setPasswordState({
                      ...passwordState,
                      show: !passwordState.show
                    })
                  }
                >
                  {passwordState.show ? <ShowIcon /> : <HideIcon />}
                </span>
              </div>
              <div
                style={{
                  marginTop: 10,
                  display: 'grid',
                  gap: 6,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--modal-border)'
                }}
              >
                {passwordRules.map((rule) => {
                  const passed = passwordChecks[rule.key];
                  return (
                    <div
                      key={rule.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        color: passed ? 'var(--accent)' : 'rgb(245, 34, 45)',
                        fontSize: 'calc(var(--x-small) * var(--font-size-multiplier))'
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: passed ? 'var(--accent)' : 'rgb(245, 34, 45)'
                        }}
                      />
                      <span>{rule.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="settings-row-action">
              <div
                style={{
                  flex: 1,
                  backgroundColor: ['very weak', 'weak'].includes(
                    passwordState.status
                  )
                    ? 'rgb(245, 34, 45)'
                    : 'var(--accent)'
                }}
              />
              <div
                style={{
                  flex: 1,
                  backgroundColor: ['medium', 'good', 'great'].includes(
                    passwordState.status
                  )
                    ? 'var(--accent)'
                    : 'var(--modal-border)'
                }}
              />
              <div
                style={{
                  flex: 1,
                  backgroundColor: ['good', 'great'].includes(
                    passwordState.status
                  )
                    ? 'var(--accent)'
                    : 'var(--modal-border)'
                }}
              />
              <div
                style={{
                  flex: 1,
                  backgroundColor:
                    passwordState.status === 'great'
                      ? 'var(--accent)'
                      : 'var(--modal-border)'
                }}
              />
            </div>
            <span
              className="form-helper"
              style={{
                textTransform: 'capitalize',
                float: 'right'
              }}
              >
              {passwordStrengthLabels[passwordState.status] || passwordState.status}
            </span>
          </div>

          <div className="modal-footer dashboard-footer">
            {!forceMode && (
              <button
                className="btn btn-cancel"
                onClick={() => setState({ open: false, userId: 0 })}
              >
                {t.buttonCancel}
              </button>
            )}
            {forceMode && (
              <button
                className="btn btn-cancel"
                onClick={() => logOut()}
              >
                {t.buttonLogout}
              </button>
            )}
            <button
              className="btn btn-apply dashboard-primary-btn"
              onClick={changePassword}
            >
              {t.buttonSave}
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};



export const Users = () => {
  const { lang } = useContext(LanguageContext);
  const tPage = userTranslations[lang];
  const { org, user, setUser, permissions } = useAuth();

  const canView = permissions['view_users'];
  const canEdit = permissions['edit_users'];
  const canSuspend = permissions['suspend_users'];
  const canTransfer = permissions['transfer_users'];
  const canChangePassword = permissions['change_user_password'];
  const canDelete = permissions['delete_users'];
  const canInvite = permissions['invite_users'];

  const isRtl = ['ar', 'ur'].includes(lang);

const resolverRef = useRef(null);
const [dialog, setDialog] = useState({
  open: false,
  type: "alert", // "alert" | "confirm"
  variant: "info", // "info" | "danger" | "error"
  icon: "alert", // "alert" | "delete"
  title: "",
  message: "",
  confirmText: "Ok",
  cancelText: "Cancel",
  showCancel: false,
});

const closeDialog = useCallback((result = false) => {
  setDialog((d) => ({ ...d, open: false }));
  if (resolverRef.current) {
    resolverRef.current(result);
    resolverRef.current = null;
  }
}, []);

const alert = useCallback(
  (opts) =>
    new Promise((resolve) => {
      resolverRef.current = resolve;

      setDialog({
        open: true,
        type: opts?.type || "alert",
        variant: opts?.variant || "info",
        icon: opts?.icon || "alert",
        title: opts?.title || "",
        message: opts?.message || "",
        confirmText: opts?.confirmText || "Ok",
        cancelText: opts?.cancelText || "Cancel",
        showCancel: Boolean(opts?.showCancel),
      });
    }),
  []
);

const showAlert = useCallback(
  async (type, message) => {
    const variant =
      type === "error" ? "error" : type === "warning" ? "info" : "info";

    await alert({
      type: "alert",
      variant,
      icon: "alert",
      title:
        type === "error"
          ? (tPage?.alert?.errorTitle || "Error")
          : type === "warning"
          ? (tPage?.alert?.warningTitle || "Warning")
          : (tPage?.alert?.infoTitle || "Info"),
      message: message || "",
      confirmText: "Ok",
    });
  },
  [alert, tPage?.alert?.errorTitle, tPage?.alert?.warningTitle, tPage?.alert?.infoTitle]
);const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editState, setEditState] = useState({ open: false, id: 0 });
  const [transfer, setTransfer] = useState({ id: 0, open: false });
  const [changePassword, setChangePassword] = useState({ open: false, userId: 0 });
  const navigate = useNavigate();
  const pagingoptions = [10, 20, 50, 100];

  const canPerformActions = canEdit || canChangePassword || canSuspend || canTransfer || canDelete;
  const baseColumns = [
    {
      field: 'icon', headerName: '', width: 60, align: 'center',
      renderCell: (params) => (<DefaultIcon name={params.row.name} />)
    },
    { field: 'name', headerName: tPage.table.name, width: 300, align: 'left', headerAlign: 'left' },
    { field: 'email', headerName: tPage.table.email, width: 300, align: 'left', headerAlign: 'left' },
    { field: 'organization_name', headerName: tPage.table.organizationName, width: 200, align: 'left', headerAlign: 'left' },
    { field: 'phone', headerName: tPage.table.phone, width: 100, align: 'center', headerAlign: 'center' },
    { field: 'role', headerName: tPage.table.role, width: 80, align: 'center', headerAlign: 'center' },
    { field: 'device_count', headerName: tPage.table.deviceCount, width: 80, align: 'center', headerAlign: 'center', type: 'number' },
    { field: 'status', headerName: tPage.table.status, width: 100, align: 'center', headerAlign: 'center' },
    {
      field: 'registered', headerName: tPage.table.registered, width: 200, align: 'center', headerAlign: 'center', type: 'dateTime',
      valueGetter: (params) => params ? new Date(params) : null,
      valueFormatter: (params) => moment(params).format('DD/MM/YYYY HH:mm:ss')
    },
    { field: 'logged', headerName: tPage.table.logged, type: 'dateTime',
      valueGetter: (params) => params ? new Date(params) : null,
      valueFormatter: (params) => moment(params).format('DD/MM/YYYY HH:mm:ss'),
       width: 200, align: 'center', headerAlign: 'center' },
    { field: 'country', headerName: tPage.table.country, width: 100, align: 'center', headerAlign: 'center' },
  ];

  const columns = canPerformActions
    ? [
      
      {
        field: 'actions', headerName: tPage.table.actions, type: 'actions', width: 100,
        getActions: (params) => {
          const actions = [];
          const isSelf = params.row.id === user.id;
          const targetRole = (params.row.role || '').toLowerCase();
          const isProtectedAccount = ['owner', 'super'].includes(targetRole);

          if (canEdit && (!isProtectedAccount || isSelf)) {
            actions.push(<GridActionsCellItem icon={<EditIcon />} showInMenu label={tPage.actions.edit} onClick={() => setEditState({ open: true, id: params.row.id })} />);
          }

          if (canChangePassword && (!isProtectedAccount || isSelf)) {
            actions.push(<GridActionsCellItem icon={<PasswordIcon color={'grey'} />} showInMenu label={tPage.actions.resetPassword} onClick={() => setChangePassword({ open: true, userId: params.row.id })} />);
          }
          
          if (canSuspend && !isProtectedAccount && !isSelf) {
            actions.push(<GridActionsCellItem icon={<SuspendIcon />} showInMenu label={tPage.actions.suspendUser} onClick={() => suspendUser(params.row.id, isSelf, () => setUser({ ...user, status: 'Suspended' }), fetchUsers, alert)} />);
          }
          
          if (canTransfer && !isProtectedAccount && !isSelf) {
            actions.push(<GridActionsCellItem icon={<TranferIcon />} showInMenu label={tPage.actions.transferUser} onClick={() => setTransfer({ id: params.row.id, open: true })} />);
          }
          
          if (canDelete && !isProtectedAccount && !isSelf) {
            actions.push(<GridActionsCellItem icon={<DeleteIcon style={{ color: 'red' }} />} showInMenu label={tPage.actions.delete} onClick={() => deleteUser(params.row.id, fetchUsers, alert)} />);
          }
          return actions;
        },
      },
      ...baseColumns,
    ]
    : baseColumns;

  const [selectedRows, setSelectedRows] = React.useState([]);
  const selectActions = canDelete
    ? [{
      title: tPage.actions.delete,
      icon: <DeleteIcon style={{ color: 'rgb(236, 69, 78)', marginInlineEnd: '20px' }} />,
      action: (ids) => {
        if (!canDelete) { showAlert('error', tPage.noPermission); return; }
        deleteMultiUsers(ids, fetchUsers, alert); setSelectedRows([]);
      }
    }]
    : [];

  const fetchUsers = useCallback(async () => {
    if (!canView || !org?.id) {
      setRows([]);
      setRowCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const activeSort = sortModel && sortModel.length > 0 ? sortModel[0] : { field: 'name', sort: 'asc' };
      const result = await axios.get(apiUrl(`user/filtered/${org.id}`), {
        params: {
          page,
          pageSize,
          sortField: activeSort.field || 'name',
          sortOrder: activeSort.sort || 'asc',
          filterModel: JSON.stringify(filterModel || { items: [] }),
        },
      });

      if (result.data && result.data.status === 'OK') {
        setRows(
          Array.isArray(result.data.data)
            ? result.data.data.map(normalizeUserListRow)
            : []
        );
        setRowCount(result.data?.pagination?.total ?? result.data?.data?.length ?? 0);
      } else {
        throw new Error('Received an invalid response from the server.');
      }
    } catch (err) {
      console.error("Fetch Users Error:", err);
      const errorMessage = getErrorMessage(err);
      showAlert('error', errorMessage);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [canView, filterModel, org?.id, page, pageSize, showAlert, sortModel]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  let colors = {
    headerBackground: 'var(--header-background)',
    headerColor: 'var(--header-color)',
    headerHover: 'var(--header-hover)',
    cellBackground: 'var(--cell-background)',
    cellColor: 'var(--cell-color)',
    cellHover: 'var(--cell-hover)',
    orderColor: 'var(--order-color)',
    orderSelectedColor: 'rgb(49, 199, 149)',
    checkboxSelection: 'var(--accent)',
    btnBackground: 'rgb(219, 245, 236)',
    btnColor: 'var(--accent)',
    modalBorder: 'var(--modal-border)',
    red: 'rgb(236, 69, 78)'
  }
  const handleRowClick = (params) => {
    navigate('/administration/users/details/' + params.row.id);
  };

  if (!canView) {
    return <div>{tPage.noPermission}</div>;
  }

  return (
    <div className="configuration devices-page" style={{ display: "flex", flexDirection: "column", height: "100%" }} dir={isRtl ? "rtl" : "ltr"}>
      <AppDialog
  open={dialog.open}
  type={dialog.type}
  variant={dialog.variant}
  icon={dialog.icon}
  title={dialog.title}
  message={dialog.message}
  confirmText={dialog.confirmText}
  cancelText={dialog.cancelText}
  showCancel={dialog.showCancel}
  onConfirm={() => closeDialog(true)}
  onCancel={() => closeDialog(false)}
  onClose={() => closeDialog(false)}
/>

      {openAddModal && canInvite &&
        <AddUser bg={colors.cellBackground}
          fg={colors.cellColor}
          bd={colors.modalBorder}
          ft={colors.headerBackground}
          btnPrimary={colors.btnBackground}
          btnSecondary={colors.btnColor}
          textColor={colors.headerColor}
          hoverOne={colors.headerHover}
          hoverTwo={colors.cellHover}
          open={openAddModal}
          setOpen={setOpenAddModal}
          refresh={fetchUsers} />
      }
      {editState.open && canEdit &&
        <EditUser bg={colors.cellBackground}
          fg={colors.cellColor}
          ft={colors.headerBackground}
          btnPrimary={colors.btnBackground}
          btnSecondary={colors.btnColor}
          state={editState}
          setState={setEditState}
          refresh={fetchUsers} />
      }
      {transfer.open && canTransfer &&
        <TransferUser state={transfer} setState={setTransfer} refresh={fetchUsers} canTransfer={canTransfer} />
      }

      {changePassword.open && canChangePassword &&
        <ChangeUserPassword state={changePassword} setState={setChangePassword} canChangePassword={canChangePassword} />
      }

      <div className="device-main">
        <div className="devices-header">
          <div className="devices-header-left">
            <h2 className="devices-title" >{tPage.usersPageTitle}</h2>
          </div>
          {canInvite &&
            <button 
                className="devices-primary-btn" 
                onClick={() => setOpenAddModal(true)}
            >
              <span>{tPage.inviteNewUser}</span>
            </button>
          }
        </div>
      
     
      {loading && rows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
          <Spinner />
        ) : rows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ?
          <div 
            className="table-wrapper" 
            style={{ 
                flex: 1, 
                minHeight: 0,
                marginTop: '20px' 
            }}
          >
            <DataGridTable columns={columns} rows={rows} idcolumn={"id"} pagingoptions={pagingoptions}
              handleRowClick={handleRowClick}
              checkboxSelection={true}
              rowSelectionModel={selectedRows} setRowSelectionModel={setSelectedRows} selectActions={selectActions}
              showToolbar={true}
              tableKey='users'
              loading={loading}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: (newPage) => setPage(newPage),
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}>
            </DataGridTable>
          </div>
          : <div style={{ 
              width: '100%', 
              flex: 1, 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center" 
          }}>
            {tPage.noData}
          </div>}
          </div>
    </div>
  );
};
